import React from 'react';
import { FieldModel } from '../../../../../models/form/field';
import TextInput from '../../../../../components/library/inputs/text/TextInput';
import NumericInput from '../../../../../components/library/inputs/numeric/NumericInput';
import CheckBox from '../../../../../components/library/inputs/checkbox/CheckboxInput';
import { noop } from '../../../../../utilities/FunctionUtilities';
import StaticTextField from '../../../../../components/library/inputs/staticText/StaticTextField';
import DocumentViewerField from '../../../../../components/library/inputs/documentViewer/DocumentViewerField';
import WeblinkField from '../../../../../components/library/inputs/weblink/WebLinkField';
import HtmlField from '../../../../../components/library/inputs/html/HtmlField';
import ImageField from '../../../../../components/library/inputs/image/ImageField';
import VideoField from '../../../../../components/library/inputs/video/VideoField';
import StarRating from '../../../../../components/library/inputs/rating/StarRating';
import Select from '../../../../../components/library/inputs/select/SelectInput';
import Toggle from '../../../../../components/library/inputs/toggle/Toggle';
import Label from '../../../../../components/library/inputs/label/Label';
import { FieldType } from '../../../../../models/form/fieldType';
import OptionList from '../../../../../components/library/inputs/optionlist/OptionList';
import Signature from '../../../../../components/library/inputs/signature/Signature';
import YesNoNa from '../../../../../components/library/inputs/yesNo/YesNoNa';
import DateTime from '../../../../../components/library/inputs/dateTime/DateTime';
import PaymentOptionList from '../../../../../components/library/inputs/payment/PaymentOptionList';
import UploadInput from '../../../../../components/library/inputs/upload/UploadInput';
import { getText } from '../../../../../utilities/CultureUtilities';
import Products from '../../../../../components/library/inputs/products/Products';

export function getFieldComponent(
  field: FieldModel,
  fieldProperties: any,
  culture: string | undefined,
): React.ReactNode {
  switch (field.type) {
    case FieldType.Text:
      return (
        <TextInput
          id={field.id}
          onChange={noop}
          value={getText(field.defaultValue, culture)}
          enabled={true}
          placeholder={getText(field.placeholder, culture)}
        ></TextInput>
      );
    case FieldType.Birthdate:
      return (
        <DateTime
          id={field.id}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          value={field.defaultValue}
          mode="birthdate"
          onClose={noop}
          onChange={noop}
        />
      );
    case FieldType.Date:
      return (
        <DateTime
          id={field.id}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          value={field.defaultValue}
          mode="date"
          onClose={noop}
          onChange={noop}
        />
      );
    case FieldType.Datetime:
      return (
        <DateTime
          id={field.id}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          value={field.defaultValue}
          mode="datetime"
          onClose={noop}
          onChange={noop}
        />
      );
    case FieldType.EmailAddress:
      return (
        <TextInput
          id={field.id}
          value={getText(field.defaultValue, culture)}
          enabled={true}
          placeholder={getText(field.placeholder, culture)}
          onChange={noop}
        ></TextInput>
      );
    case FieldType.LongText:
      return (
        <TextInput
          id={field.id}
          value={getText(field.defaultValue, culture)}
          enabled={true}
          onChange={noop}
          placeholder={getText(field.placeholder, culture)}
          rest={{
            rows: 3,
            as: 'textarea',
          }}
        />
      );
    case FieldType.Numeric:
      return (
        <NumericInput
          id={field.id}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          value={field.defaultValue}
          onChange={noop}
        ></NumericInput>
      );
    case FieldType.OptionList:
      return (
        <OptionList
          options={field.options.map((option) => {
            return {
              text: <span>{getText(option.text, culture)}</span>,
              value: option.value,
            };
          })}
          multiSelection={field.multiSelection}
          value={field.defaultValue ? field.defaultValue : []}
          onChange={noop}
        />
      );
    case FieldType.Select:
      return (
        <Select
          culture={culture}
          id={field.id}
          onChange={noop}
          value={field.defaultValue}
          options={field.options}
          placeholder={getText(field.placeholder, culture)}
        />
      );
    case FieldType.StaticText:
      return <StaticTextField textColor={field.textColor} value={getText(field.value, culture)} />;
    case FieldType.Time:
      return (
        <DateTime
          id={field.id}
          enabled={field.enabled}
          placeholder={getText(field.placeholder, culture)}
          value={field.defaultValue}
          mode="time"
          onClose={noop}
          onChange={noop}
        />
      );
    case FieldType.CheckBox:
      return (
        <CheckBox
          label={getText(field.text, culture)}
          id={field.id}
          value={field.defaultValue}
          enabled={true}
          onChange={noop}
        />
      );
    case FieldType.Toggle:
      return (
        <Toggle
          label={
            <Label
              label={getText(field.text, culture)}
              required={field.required}
              textColor={undefined}
              hidden={false}
            />
          }
          disabled={!field.enabled}
          id={field.id}
          checked={field.defaultValue}
          onChange={noop}
        />
      );
    case FieldType.WebLink:
      return (
        <WeblinkField
          value={getText(field.defaultValue, culture)}
          enabled={field.enabled}
          target={field.target}
          text={field.placeholder ? getText(field.placeholder, culture) : 'default'}
          textColor={field.textColor}
        />
      );
    case FieldType.Rating:
      return (
        <div>
          <StarRating
            color={field.starColor}
            value={field.defaultValue ? field.defaultValue : 0}
            count={field.count}
            fontSize={30}
            allowHalf={field.allowHalf}
            onChange={noop}
          />
        </div>
      );
    case FieldType.YesNo:
      return <YesNoNa value={field.defaultValue} enabled={field.enabled} allowNa={field.allowNa} onChange={noop} />;
    case FieldType.Acknowledgment:
      return (
        <CheckBox
          label={getText(field.text, culture)}
          id={field.id}
          value={field.defaultValue}
          enabled={true}
          onChange={noop}
        />
      );
    case FieldType.HTML:
      return <HtmlField value={field.value} />;
    case FieldType.Signature:
      return <Signature onChange={noop} onTouched={noop} />;
    case FieldType.DocumentViewer:
      return (
        <DocumentViewerField
          value={getText(field.defaultValue, culture)}
          documentName={getText(field.documentName, culture)}
          documentType={field.documentType}
        />
      );
    case FieldType.EmbeddedPhoto:
      return <ImageField src={getText(field.defaultValue, culture)} alt={getText(field.alt, culture)} />;
    case FieldType.Video:
      return <VideoField src={getText(field.defaultValue, culture)} videoPlayer={field.videoPlayer} />;
    case FieldType.PaymentOptionList:
      return (
        <PaymentOptionList
          fieldId={field.id}
          fieldProperties={fieldProperties}
          value={field.defaultValue ? field.defaultValue : []}
          culture={culture}
          options={field.options}
          onChange={noop}
          multiSelection={false}
        />
      );
    case FieldType.PaymentChoiceList:
      return (
        <PaymentOptionList
          fieldId={field.id}
          fieldProperties={fieldProperties}
          culture={culture}
          value={field.defaultValue ? field.defaultValue : []}
          onChange={noop}
          options={field.choices}
          multiSelection={true}
        />
      );
    case FieldType.UploadFile:
      return <UploadInput id={field.id} disabled={!field.enabled} value={''} onChange={noop} />;
    case FieldType.Products:
      return (
        <Products
          fieldId={field.id}
          culture={culture}
          value={[]}
          onChange={noop}
          products={field.products}
          fieldProperties={fieldProperties}
        />
      );
  }
}
